import { alleKarten } from '../karten/karten'
import type { Kartenstand } from '../scheduler/typen'
import {
  HERDE_SCHWELLEN,
  RITT_ANKUNFT_MAX_MS,
  RITT_ANKUNFT_MIN_MS,
  RITT_ANKUNFT_PUFFER,
} from '../config'
import { HERDENPFERDE } from './pferde'

/** 0 versteckt, 1 Spuren, 2 Gesichtet, 3 Vertrauen, 4 Heimgekehrt. */
export type Stufe = 0 | 1 | 2 | 3 | 4

export const STUFEN_TEXT: Record<Stufe, string> = {
  0: 'Noch versteckt',
  1: 'Spuren entdeckt',
  2: 'Gesichtet',
  3: 'Vertraut dir',
  4: 'Heimgekehrt',
}

/**
 * Die zehn Karten einer Reihe.
 *
 * Eine Karte ist ein ungeordnetes Faktorenpaar, 6 × 7 steckt also in der
 * 6er- und in der 7er-Reihe. Ein schneller Abruf hilft damit zwei Pferden.
 */
export function kartenDerReihe(reihe: number): string[] {
  return alleKarten()
    .filter((k) => k.a === reihe || k.b === reihe)
    .map((k) => k.id)
}

/**
 * Wie weit das Pferd einer Reihe ist, allein aus den Kartenständen.
 *
 * Kein Zähler, kein Punktestand: Die Stufe ist eine Übersetzung dessen, was
 * die Messwerte belegen.
 */
export function stufeFuer(reihe: number, staende: Kartenstand[]): Stufe {
  const ids = new Set(kartenDerReihe(reihe))
  const eigene = staende.filter((s) => ids.has(s.karte))
  const mitFach = (fach: number) => eigene.filter((s) => s.fach >= fach).length
  const { gesichtet, vertrauen, heimgekehrt } = HERDE_SCHWELLEN

  if (mitFach(heimgekehrt.fach) >= heimgekehrt.anzahl) return 4
  if (mitFach(vertrauen.fach) >= vertrauen.anzahl) return 3
  if (mitFach(gesichtet.fach) >= gesichtet.anzahl) return 2
  if (eigene.some((s) => !s.erstkontakt)) return 1
  return 0
}

export function herdeStufen(staende: Kartenstand[]): Record<number, Stufe> {
  const stufen: Record<number, Stufe> = {}
  for (const p of HERDENPFERDE) stufen[p.reihe] = stufeFuer(p.reihe, staende)
  return stufen
}

/**
 * Eine erreichte Stufe geht nie verloren.
 *
 * Fällt eine Reihe zurück, läuft das Pferd nicht weg. Es kommt stattdessen
 * öfter mit auf Ausritte, weil der Scheduler die Karten häufiger stellt.
 */
export function mitGedaechtnis(
  neu: Record<number, Stufe>,
  alt: Record<number, Stufe> | undefined,
): Record<number, Stufe> {
  const ergebnis: Record<number, Stufe> = { ...neu }
  for (const [reihe, stufe] of Object.entries(alt ?? {})) {
    const r = Number(reihe)
    const jetzt = ergebnis[r] ?? 0
    if (stufe > jetzt) ergebnis[r] = stufe
  }
  return ergebnis
}

/** Wie lange das Hindernis braucht, bis es am Haltepunkt ist. */
export function ankunftszeitMs(stand: Kartenstand | undefined): number {
  if (!stand || stand.erstkontakt) return RITT_ANKUNFT_MAX_MS
  const ziel = Math.round(stand.letzteZeitMs * RITT_ANKUNFT_PUFFER)
  return Math.min(RITT_ANKUNFT_MAX_MS, Math.max(RITT_ANKUNFT_MIN_MS, ziel))
}
